import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import camelCase from "lodash/camelCase";
import isArray from "lodash/isArray";
import isPlainObject from "lodash/isPlainObject";
import snakeCase from "lodash/snakeCase";
import { API_CACHING_TAGS } from "../../constants/api";
import { BASE_URL } from "../apiEndPoints";
import { logout } from "../slices/authSlice";
import { RootState } from "../store";

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const authToken = (getState() as RootState).auth.token;
    if (authToken) {
      headers.set("authorization", `Bearer ${authToken}`);
    }
    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  // logout on 401 except the destination connection tests (a 401 there is the destination's, not our session's)
  if (result.error && result.error.status === 401) {
    const url = (args as FetchArgs)?.url ?? "";
    const isDestinationTestPath =
      url.includes("/destination-connection-test") ||
      /\/destinations\/[^/?]+\/test(\?|$)/.test(url);
    if (!isDestinationTestPath) {
      api.dispatch(logout());
    }
  }

  return result;
};

export const api = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: API_CACHING_TAGS,
  endpoints: () => ({}),
});

export const convertKeysToCamelCase = (obj: any): any => {
  if (isArray(obj)) {
    return obj.map(convertKeysToCamelCase);
  } else if (isPlainObject(obj)) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        camelCase(key),
        convertKeysToCamelCase(value),
      ])
    );
  }
  return obj;
};

export const convertKeysToSnakeCase = (obj: any): any => {
  if (isArray(obj)) {
    return obj.map(convertKeysToSnakeCase);
  } else if (isPlainObject(obj)) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        snakeCase(key),
        convertKeysToSnakeCase(value),
      ])
    );
  }
  return obj;
};
